/** external */
import React, { useState, useCallback, Fragment } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import _ from "lodash";
import classnames from "classnames";
import { IconButton } from "@toddle-edu/ds-web";
import { AddOutlined } from "@toddle-edu/ds-icons";

/** internal */
import {
  WORKSHEET_ELEMENT_IDS,
  FIELD_UID_TITLE,
  QUESTION_UID_SEPARATOR,
} from "Worksheet/modules/WorksheetConstants";

/** relative */
import SectionHeader from "../SectionHeader";
import QuestionDetails from "../../QuestionDetails";
import {
  getSectionDetailsV2,
  getQuestionIndexV2,
} from "../SinglePageQuestionEditorUtils";
import classes from "./QuestionPane.scss";

/**
 * QuestionPane component renders sections and questions in a worksheet editor.
 * It handles the display of questions, sections, and provides functionality for
 * adding questions in between existing questions.
 */
const QuestionPane = ({
  sections,
  mode,
  t,
  isDraft,
  courseId,
  portalType,
  sourceFrom,
  parentId,
  unitPlanId,
  creationMode,
  classSubjects,
  setQuestionRef,
  selectedCourse,
  isFromUnitPlan,
  unitPlanTemplateId,
  worksheetDetailsRef,
  showWorksheetAssistant,
  getLearningGoalsMappingWrapper,
  activeQuestionId,
  isAIAssessmentToolsOpen,
  isGeneratingAIQuestions,
  getAddButton,
  spaceContainerInBetweenRef,
  updateAddQuestionsInBetween,
  nextQuestionId,
}) => {
  const [addButtonActiveIds, setAddButtonActiveIds] = useState([]);
  const [addButtonDropdownActiveId, setAddButtonDropdownActiveId] = useState(
    null
  );

  /**
   * Handles the logic for adding questions in between existing questions.
   * This function manages the state and calls the parent update function.
   */
  const handleAddQuestionsInBetween = useCallback(
    ({
      questionId,
      parentItemId,
      nextQuestionId,
      isAddingAtStartOfMultipart = false,
    }) => {
      let _parentItemId = parentItemId;
      // Distinguish start-of-multipart from after-multipart (same parent id) to avoid toggle conflict
      if (isAddingAtStartOfMultipart) {
        _parentItemId += "multipart";
      }
      setAddButtonActiveIds(prev => [...prev, questionId ?? _parentItemId]);
      updateAddQuestionsInBetween({
        isAddingQuestionsInBetween: true,
        isAddingAtStartOfMultipart,
        questionId,
        parentItemId,
        nextQuestionId,
      });
    },
    [updateAddQuestionsInBetween]
  );

  /**
   * Handles mouse leave events for add buttons.
   * Removes the ID from active buttons unless dropdown is active.
   */
  const onMouseLeave = useCallback(
    ({ id }) => {
      if (addButtonDropdownActiveId === id) return;
      setAddButtonActiveIds(prev => prev.filter(activeId => activeId !== id));
    },
    [addButtonDropdownActiveId]
  );

  /**
   * Handles blur events for add buttons.
   * Removes the ID from active buttons if focus has left the container.
   */
  const onBlur = useCallback(({ e, id }) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setAddButtonActiveIds(prev => prev.filter(activeId => activeId !== id));
  }, []);

  // Configuration object for add questions in between functionality
  const addQuestionsInBetweenProps = {
    forSidebar: false,
    isRevampedSinglePageUI: true,
    addButtonActiveIds,
    showAddSection: false,
    showAddSeparator: true,
    showAddInlineText: true,
    isAddDropdownButtonRevamp: true,
    // Custom button component for add functionality
    btnComponent: (
      <div className={classes.addInBetweenIcon}>
        <IconButton
          icon={<AddOutlined />}
          variant="neutral"
          size="small"
          type="outlined"
          dsVersion="2.0"
          disabled={isGeneratingAIQuestions}
          testId="worksheet-addQuestionsDropdown-addQuestionsInBetween"
        />
      </div>
    ),
    handleAddQuestionsInBetween: args => handleAddQuestionsInBetween(args),
    onMouseLeave: args => onMouseLeave(args),
    onBlur: args => onBlur(args),
    setAddButtonDropdownActiveId,
    addButtonDropdownActiveId,
  };

  // Properties to omit when passing props to child components
  const addQuestionsInBetweenPropsToOmit = [
    "handleAddQuestionsInBetween",
    "onMouseLeave",
    "onBlur",
    "addButtonActiveIds",
    "addButtonDropdownActiveId",
  ];

  return (
    <>
      {/* Render each section and its questions */}
      {_.map(sections, (section, sectionIdx) => {
        const sectionId = _.get(section, "item.id", null);
        const parentItemId = _.get(section, "item.id", null);
        const { sectionScore, numberOfQuestions } = getSectionDetailsV2(
          section
        );
        const isLastSection = sectionIdx === _.size(sections) - 1;

        let lastQuestionId = null;
        if (isLastSection && !_.isEmpty(section)) {
          const lastQuestion = _.last(_.get(section, "children", []));
          lastQuestionId = _.get(lastQuestion, "item.id", "");
        }

        const questions = _.get(section, "children", []);

        const showAddQuestionAtTop =
          !isGeneratingAIQuestions && (!isLastSection || !_.isEmpty(questions));

        const titleFieldId = _.get(
          _.find(_.get(section, "item.fields", []), {
            uid: FIELD_UID_TITLE,
          }),
          "id",
          null
        );

        const sectionLabel = _.get(
          _.find(_.get(section, "item.fields", []), {
            uid: FIELD_UID_TITLE,
          }),
          "value",
          null
        );

        const firstQuestionId = _.get(_.first(questions, {}), "item.id", "");

        const isAddButtonActive = _.includes(addButtonActiveIds, sectionId);

        const addInBetweenClasses = classnames({
          [classes.addInBetween]: true,
          [classes.addInBetweenActive]: isAddButtonActive,
          [classes.addInBetweenDropdownActive]:
            addButtonDropdownActiveId === sectionId,
          [classes.disabledAddInBetween]:
            showWorksheetAssistant || isAIAssessmentToolsOpen,
        });

        return (
          <Fragment key={sectionId}>
            {/* Render section header */}
            <SectionHeader
              mode={mode}
              id={sectionId}
              label={sectionLabel}
              sectionScore={sectionScore}
              numberOfQuestions={numberOfQuestions}
              sectionMarks={0}
              isGeneratingAIQuestions={isGeneratingAIQuestions}
              parentItemId={parentItemId}
              titleFieldId={titleFieldId}
            />

            {/* Add question button at the top of section */}
            {showAddQuestionAtTop && (
              <div
                className={addInBetweenClasses}
                onMouseEnter={() =>
                  handleAddQuestionsInBetween({
                    questionId: null,
                    parentItemId: sectionId,
                    nextQuestionId: firstQuestionId,
                  })
                }
                onMouseLeave={() => onMouseLeave({ id: sectionId })}
                onBlur={e => onBlur({ e, id: sectionId })}
                id={WORKSHEET_ELEMENT_IDS.WORKSHEET_ADD_QUESTION_IN_BETWEEN_ID}
              >
                {isAddButtonActive &&
                  getAddButton({
                    ..._.omit(addQuestionsInBetweenProps, [
                      ...addQuestionsInBetweenPropsToOmit,
                    ]),
                  })}
              </div>
            )}

            {/* Render all questions in this section */}
            {_.map(questions, (question, index, allQuestions) => {
              const questionId = _.get(question, "item.id", "");
              const questionTypeUid = _.get(
                question,
                "item.questionType.uid",
                ""
              );

              const questionIndex = getQuestionIndexV2(sections, questionId);
              const isActive = questionId === activeQuestionId;

              const isLastSectionLastQuestion =
                _.get(question, "item.id", "") === lastQuestionId;

              const showAddQuestionInBetween =
                !isLastSectionLastQuestion && !isGeneratingAIQuestions;

              // Get the next question's id
              const nextQuestion = allQuestions[index + 1];
              const _nextQuestionId = _.get(nextQuestion, "item.id", "");

              const spaceContainerInBetween =
                questionId === nextQuestionId && isGeneratingAIQuestions;

              const isAddButtonActive = _.includes(
                addButtonActiveIds,
                questionId
              );

              const addInBetweenClasses = classnames({
                [classes.addInBetween]: true,
                [classes.addInBetweenActive]: isAddButtonActive,
                [classes.addInBetweenDropdownActive]:
                  addButtonDropdownActiveId === questionId,
                [classes.disabledAddInBetween]:
                  showWorksheetAssistant || isAIAssessmentToolsOpen,
              });

              if (questionIndex === -1) return null;

              return (
                <Fragment key={questionId}>
                  {/* Render individual question */}
                  <QuestionDetails
                    // Common props passed to all questions
                    t={t}
                    mode={mode}
                    key={questionId}
                    isDraft={isDraft}
                    courseId={courseId}
                    portalType={portalType}
                    sourceFrom={sourceFrom}
                    assessmentId={parentId}
                    unitPlanId={unitPlanId}
                    creationMode={creationMode}
                    classSubjects={classSubjects}
                    setQuestionRef={setQuestionRef}
                    selectedCourse={selectedCourse}
                    isFromUnitPlan={isFromUnitPlan}
                    unitPlanTemplateId={unitPlanTemplateId}
                    worksheetDetailsRef={worksheetDetailsRef}
                    showWorksheetAssistant={showWorksheetAssistant}
                    getLearningGoalsMappingWrapper={
                      getLearningGoalsMappingWrapper
                    }
                    // Question specific props
                    isActive={isActive}
                    sectionId={sectionId}
                    parentItemId={parentItemId} // section's item id
                    questionIndex={questionIndex}
                    isAIAssessmentToolsOpen={isAIAssessmentToolsOpen}
                    isSeparatorQuestion={
                      questionTypeUid === QUESTION_UID_SEPARATOR
                    }
                    // Conditionally add space container ref for AI generation
                    {...(spaceContainerInBetween && {
                      spaceContainerInBetweenRef,
                    })}
                    getAddButton={getAddButton}
                    addQuestionsInBetweenProps={addQuestionsInBetweenProps}
                    question={question}
                    isQueriesV2Enabled={true} // Assuming queries V2 is always enabled here
                  />

                  {/* Add question button after each question (except last) */}
                  {showAddQuestionInBetween && (
                    <div
                      className={addInBetweenClasses}
                      onMouseEnter={() =>
                        handleAddQuestionsInBetween({
                          questionId,
                          parentItemId: sectionId,
                          nextQuestionId: _nextQuestionId,
                        })
                      }
                      onMouseLeave={() => onMouseLeave({ id: questionId })}
                      onBlur={e => onBlur({ e, id: questionId })}
                      id={
                        WORKSHEET_ELEMENT_IDS.WORKSHEET_ADD_QUESTION_IN_BETWEEN_ID
                      }
                    >
                      {isAddButtonActive &&
                        getAddButton({
                          ..._.omit(addQuestionsInBetweenProps, [
                            ...addQuestionsInBetweenPropsToOmit,
                          ]),
                        })}
                    </div>
                  )}
                </Fragment>
              );
            })}
          </Fragment>
        );
      })}
    </>
  );
};

const mapStateToProps = state => {
  const nextQuestionId = _.get(
    state,
    "worksheet.addQuestionsInBetween.nextQuestionId",
    null
  );
  return {
    nextQuestionId,
  };
};

QuestionPane.propTypes = {
  sections: PropTypes.array.isRequired,
  mode: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  isDraft: PropTypes.bool,
  courseId: PropTypes.string,
  portalType: PropTypes.string,
  sourceFrom: PropTypes.string,
  parentId: PropTypes.string,
  unitPlanId: PropTypes.string,
  creationMode: PropTypes.string,
  classSubjects: PropTypes.array,
  setQuestionRef: PropTypes.func,
  selectedCourse: PropTypes.object,
  isFromUnitPlan: PropTypes.bool,
  unitPlanTemplateId: PropTypes.string,
  worksheetDetailsRef: PropTypes.object,
  showWorksheetAssistant: PropTypes.bool,
  getLearningGoalsMappingWrapper: PropTypes.func,
  activeQuestionId: PropTypes.string,
  isAIAssessmentToolsOpen: PropTypes.bool,
  isGeneratingAIQuestions: PropTypes.bool,
  getAddButton: PropTypes.func.isRequired,
  spaceContainerInBetweenRef: PropTypes.object,
  updateAddQuestionsInBetween: PropTypes.func.isRequired,
};

QuestionPane.defaultProps = {
  isDraft: false,
  courseId: "",
  portalType: "",
  sourceFrom: "",
  parentId: "",
  unitPlanId: "",
  creationMode: "",
  classSubjects: [],
  setQuestionRef: () => {},
  selectedCourse: {},
  isFromUnitPlan: false,
  unitPlanTemplateId: "",
  worksheetDetailsRef: {},
  showWorksheetAssistant: false,
  getLearningGoalsMappingWrapper: () => {},
  activeQuestionId: "",
  isAIAssessmentToolsOpen: false,
  isGeneratingAIQuestions: false,
  spaceContainerInBetweenRef: null,
};

export default connect(mapStateToProps, null)(QuestionPane);
